import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { Campaign, CampaignStatus } from "schemas/dashboard";

import { CampaignStatusChip } from "features/Influencer/Campaigns/CampaignStatusChip";
import { arrayQuery, fetcherAuth } from "utils/api";
import { formatDate } from "utils/time";

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (campaign: Campaign) => void;
  currentCampaignId?: number;
}

export default function PreviousCampaignDialog({
  open,
  onClose,
  onSelect,
  currentCampaignId,
}: Props) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!currentOrg || !open) return;
      setLoading(true);
      try {
        const statusQuery = arrayQuery([CampaignStatus.complete], "statuses");
        const { campaigns: fetched } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/campaigns?per_page=100${statusQuery}`,
          "GET",
        );
        setCampaigns(
          (fetched ?? []).filter((c: Campaign) => c.id !== currentCampaignId),
        );
      } catch (error) {
        setErrorAlert(error);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 700,
        }}
      >
        Choose a previous campaign
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <Box component="i" className="fa-regular fa-xmark" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "60vh" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : campaigns.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: "text.secondary", py: 2 }}>
            You don&apos;t have any past campaigns to repitch yet.
          </Typography>
        ) : (
          campaigns.map((c) => (
            <Box
              key={c.id}
              onClick={() => onSelect(c)}
              sx={{
                p: 1.5,
                mb: 1,
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 2,
                cursor: "pointer",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "rgba(1,67,57,0.02)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                  {c.name}
                </Typography>
                {c.status && (
                  <CampaignStatusChip
                    status={c.status}
                    startDate={c.startDate ?? undefined}
                  />
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  mt: 0.75,
                  fontSize: 12,
                  color: "text.secondary",
                }}
              >
                {c.startDate && (
                  <Meta icon="fa-regular fa-calendar">
                    {formatDate(c.startDate)}
                    {c.endDate ? ` – ${formatDate(c.endDate)}` : ""}
                  </Meta>
                )}
                {c.campaignBrandCount !== undefined && (
                  <Meta icon="fa-regular fa-users">
                    {c.campaignBrandCount} brand
                    {c.campaignBrandCount === 1 ? "" : "s"}
                  </Meta>
                )}
                {!!c.emailsPerDay && (
                  <Meta icon="fa-regular fa-envelope">
                    {c.emailsPerDay}/day
                  </Meta>
                )}
              </Box>
            </Box>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
}

function Meta({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Box component="i" className={icon} sx={{ fontSize: 11 }} />
      {children}
    </Box>
  );
}
